import { useCollectionsStore } from '../store/collectionsStore';
import { apiClientKey, getStorageNamespace } from './storageNamespace';

/**
 * Request History <-> localStorage, tied to the storage namespace it came from
 * (card b9799baf, 26.09.2026).
 *
 * The store starts with `history: []`. The save effect used to write whatever
 * the store held on every change — including that initial `[]` on the first
 * render, BEFORE the loader (which waits for auth to initialise) had read the
 * saved list. Whenever auth was not ready on the first render the saved
 * History was replaced by `[]` and then "loaded" empty. After a sign-in or
 * sign-out the same effect also wrote one account's History into the other
 * namespace's key.
 *
 * Now the store remembers which namespace its History was loaded for
 * (`historyNs`), and `persistHistory` writes only when that is the current
 * namespace. Nothing is written until a load has happened.
 */

function readSaved(ns) {
  try {
    const parsed = JSON.parse(localStorage.getItem(apiClientKey('history', ns)) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Load the current namespace's History into the store, unless it already is. */
export function loadHistoryForCurrentNamespace() {
  const ns = getStorageNamespace();
  if (useCollectionsStore.getState().historyNs === ns) return;
  useCollectionsStore.setState({ history: readSaved(ns), historyNs: ns });
}

/**
 * Save `history` under `historyNs`. A no-op before the first load and while the
 * store still holds another namespace's History. Responses can be large, so a
 * full quota degrades to no response bodies, then to a 20-entry tail.
 */
export function persistHistory(history, historyNs) {
  if (!historyNs || historyNs !== getStorageNamespace()) return;
  const key = apiClientKey('history', historyNs);
  try {
    localStorage.setItem(key, JSON.stringify(history));
  } catch {
    try {
      const withoutResponses = history.map((h) => (h.response ? { ...h, response: null } : h));
      localStorage.setItem(key, JSON.stringify(withoutResponses));
    } catch {
      try {
        const tail = history.slice(0, 20).map((h) => ({ ...h, response: null }));
        localStorage.setItem(key, JSON.stringify(tail));
      } catch (err) {
        console.warn('[history] unable to persist request history (storage full)', err);
      }
    }
  }
}
